import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { gallerySlides } from '../data/gallery';
const easeOut = [0.23, 1, 0.32, 1] as const;
export function ProductGallery() {
  const [[index, direction], setState] = useState<[number, number]>([0, 0]);
  const [isZoomed, setIsZoomed] = useState(false);
  const slide = gallerySlides[index];
  const paginate = useCallback((step: number) => {
    setState(([current]) => {
      const next = (current + step + gallerySlides.length) % gallerySlides.length;
      return [next, step];
    });
  }, []);
  const goTo = useCallback((next: number) => {
    setState(([current]) => [next, next > current ? 1 : -1]);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') paginate(1);
      if (event.key === 'ArrowLeft') paginate(-1);
      if (event.key === 'Escape') setIsZoomed(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [paginate]);
  return <section className="py-20 px-4 bg-stone-50" data-id="element-207" data-mp="2zssblzvnhgae">
      <div className="max-w-5xl mx-auto" data-id="element-208" data-mp="3dqpch8h508uv">
        <div className="text-center mb-10" data-id="element-209" data-mp="3docvoyn484je">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-blue-950" data-id="element-210" data-mp="38qun59d0frbx">
            See Inside Driving Instructor Ready
          </h2>
          <p className="text-lg text-slate-700 max-w-3xl mx-auto" data-id="element-211" data-mp="2l21o3413d3d3">
            Real pages from the system — visual maneuvers, teaching sequences, instructor cues,
            corrections and practical driving lesson tools.
          </p>
        </div>

        <div className="relative max-w-3xl mx-auto" data-id="element-212" data-mp="2q0mlkvoyl8xu">
          <div className="relative bg-white border border-stone-200 rounded-2xl shadow-sm shadow-blue-950/10 overflow-hidden" role="group" aria-roledescription="carousel" aria-label="Product page previews" data-id="element-213" data-mp="2pys09g9rzet8">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-214" data-mp="189m0vcoljct">
              <motion.div key={slide.src} custom={direction} initial={{
              opacity: 0,
              x: direction > 0 ? 40 : -40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction > 0 ? -40 : 40
            }} transition={{
              duration: 0.25,
              ease: easeOut
            }} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} dragElastic={0.15} onDragEnd={(_, info) => {
              if (info.offset.x < -60) paginate(1);else if (info.offset.x > 60) paginate(-1);
            }} className="p-4 sm:p-8 cursor-zoom-in touch-pan-y" onClick={() => setIsZoomed(true)} aria-roledescription="slide" aria-label={`${index + 1} of ${gallerySlides.length}`} data-id="element-215" data-mp="67dtnhjyj911">
                <img src={slide.src} alt={slide.alt} draggable={false} className="w-full h-auto max-h-[70vh] object-contain mx-auto select-none rounded-lg" data-id="element-216" data-mp="2s6xh8mxqvx5n" />
              </motion.div>
            </AnimatePresence>

            <button type="button" onClick={() => setIsZoomed(true)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-blue-950/90 text-white flex items-center justify-center hover:bg-blue-950 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="View image larger" data-id="element-217" data-mp="2eg0uvduewc71">
              <Maximize2 size={18} data-id="element-218" data-mp="1vhy9ra4zizei" />
            </button>
          </div>

          <button type="button" onClick={() => paginate(-1)} className="absolute left-0 sm:-left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-blue-950 text-white border-2 border-orange-500 shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="Previous image" data-id="element-219" data-mp="3utqwmdy26odi">
            <ChevronLeft size={24} data-id="element-220" data-mp="2dlg6axzj52gl" />
          </button>
          <button type="button" onClick={() => paginate(1)} className="absolute right-0 sm:-right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-blue-950 text-white border-2 border-orange-500 shadow-lg flex items-center justify-center hover:bg-blue-700 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="Next image" data-id="element-221" data-mp="2ihnaomca25wf">
            <ChevronRight size={24} data-id="element-222" data-mp="1kuocg75xspwf" />
          </button>
        </div>

        <div className="flex justify-center items-center gap-3 mt-6" data-id="element-223" data-mp="1kvg81hzjanmp">
          {gallerySlides.map((item, dotIndex) => <button key={item.src} type="button" onClick={() => goTo(dotIndex)} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 ${dotIndex === index ? 'w-8 bg-orange-500' : 'w-2.5 bg-blue-950/25 hover:bg-blue-950/40'}`} aria-label={`Go to image ${dotIndex + 1}`} aria-current={dotIndex === index} data-id="element-224" data-mp="1pvlwyqef5w13" data-mpi={dotIndex} />)}
        </div>

        <p className="text-center text-lg font-serif font-bold text-blue-950 mt-10" data-id="element-225" data-mp="1k7gwo6a4izsj">
          Real pages. Real teaching tools.{' '}
          <span className="text-orange-600" data-id="element-226" data-mp="1txjy8uvb9m2h">A system built to be used — not just read.</span>
        </p>
      </div>

      <AnimatePresence data-id="element-227" data-mp="27wtetvnfii6z">
        {isZoomed && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: easeOut
      }} className="fixed inset-0 z-50 bg-blue-950/95 flex items-center justify-center p-4 overflow-auto" role="dialog" aria-modal="true" aria-label={slide.alt} onClick={() => setIsZoomed(false)} data-id="element-228" data-mp="27xl12fv4z3kl">
            <button type="button" onClick={() => setIsZoomed(false)} className="fixed top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500" aria-label="Close enlarged image" data-id="element-229" data-mp="2poc85dw2n7tt">
              <X size={22} data-id="element-230" data-mp="38ln7eyhpe199" />
            </button>
            <motion.img initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: easeOut
        }} src={slide.src} alt={slide.alt} className="max-w-full md:max-w-3xl h-auto rounded-xl shadow-2xl" onClick={event => event.stopPropagation()} data-id="element-231" data-mp="38kc0wqwvifeg" />
          </motion.div>}
      </AnimatePresence>
    </section>;
}